import { useState } from "react";
import { useApi, apiPost } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { timeAgo, fmtDate, VENDOR_LABEL } from "@/lib/format";
import { Plug, CheckCircle2, ArrowRight, ArrowLeft, KeyRound, Loader2 } from "lucide-react";

export default function IntegrationHub() {
  const { data: connectors = [], isLoading, error, refetch } = useApi("connectors", "/connectors");
  const [wizard, setWizard] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [testing, setTesting] = useState(false);

  const openWizard = (c) => { setWizard(c); setStep(0); setForm({ ...(c.config || {}) }); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const testConnector = async (c, values) => {
    setTesting(true);
    try {
      const r = await apiPost(`/connectors/${c.id}/test`, { values: values || {} });
      r.ok ? toast.success(r.message) : toast.error(r.message);
      return r.ok;
    } catch (e) { toast.error("Test failed — unexpected error."); return false; }
    finally { setTesting(false); }
  };

  const finish = async () => {
    await apiPost(`/connectors/${wizard.id}/configure`, { values: form });
    toast.success(`${wizard.name} configured & connected`);
    setWizard(null); refetch();
  };

  if (error) return <ErrorState />;

  return (
    <div>
      <PageHeader eyebrow="Navitra Hub" title="Integration Hub" subtitle="15 configuration packs (CP-01–CP-14). Configure each connector's parameters, then test the live connection." />
      {isLoading ? <LoadingBlock /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {connectors.map((c) => (
            <Card key={c.id} data-testid={`connector-${c.id}`} className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2"><span className="mono text-xs text-gold">{c.id}</span><SeverityChip level={c.status} dot /></div>
                  <SeverityChip level="neutral" label={c.configured ? "configured" : "not set up"} />
                </div>
                <CardTitle className="text-base mt-2">{c.name}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm text-muted-foreground flex-1">{c.summary}</p>
                <div className="text-xs text-muted-foreground mt-3 space-y-0.5">
                  <div className="flex justify-between"><span>Parameters</span><span className="mono">{(c.param_schema || []).length} fields</span></div>
                  <div className="flex justify-between"><span>Last sync</span><span className="mono">{timeAgo(c.last_sync)}</span></div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openWizard(c)} data-testid={`setup-${c.id}`}><Plug className="h-3.5 w-3.5 mr-1.5" />{c.configured ? "Configure" : "Set up"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => testConnector(c)} data-testid={`test-${c.id}`}>Test</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!wizard} onOpenChange={(o) => !o && setWizard(null)}>
        <DialogContent className="max-w-lg">
          {wizard && (<>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><span className="mono text-sm text-gold">{wizard.id}</span>{wizard.name}</DialogTitle></DialogHeader>
            <Progress value={((step + 1) / wizard.wizard_steps.length) * 100} className="[&>div]:bg-navy" />
            <div className="flex justify-between text-xs text-muted-foreground">{wizard.wizard_steps.map((s, i) => <span key={s} className={i === step ? "text-gold font-semibold" : i < step ? "text-sev-success" : ""}>{s}</span>)}</div>

            <div className="rounded-lg border bg-muted/30 p-5 min-h-[180px]">
              <p className="font-display text-lg mb-3 flex items-center gap-2">{step > 0 && <CheckCircle2 className="h-4 w-4 text-sev-success" />}Step {step + 1}: {wizard.wizard_steps[step]}</p>

              {step === 0 && <p className="text-sm text-muted-foreground">Readiness checks for {VENDOR_LABEL[wizard.vendor] || wizard.vendor}: licences, roles and audit settings verified automatically. We show exactly what's missing and who usually owns it.</p>}

              {step === 1 && (
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>Grant least-privilege access. Scopes requested: <b className="text-foreground">{(wizard.scopes || []).join(", ")}</b>.</p>
                  <p className="flex items-start gap-1.5"><KeyRound className="h-4 w-4 text-gold mt-0.5 shrink-0" />Secrets are stored in Key Vault via secretRef — never in plain config.</p>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-3" data-testid="connector-param-form">
                  <p className="text-xs text-muted-foreground">Enter the connection parameters for {wizard.name}. Required fields are marked with *.</p>
                  {(wizard.param_schema || []).map((f) => (
                    <div key={f.key} className="space-y-1">
                      <Label htmlFor={`f-${f.key}`} className="text-xs">{f.label}{f.required && <span className="text-sev-danger"> *</span>}</Label>
                      {f.type === "select" ? (
                        <Select value={form[f.key] || ""} onValueChange={(v) => setField(f.key, v)}>
                          <SelectTrigger id={`f-${f.key}`} data-testid={`param-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input id={`f-${f.key}`} data-testid={`param-${f.key}`}
                          type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"}
                          placeholder={f.placeholder} value={form[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} />
                      )}
                      {f.help && <p className="text-[11px] text-muted-foreground">{f.help}</p>}
                    </div>
                  ))}
                </div>
              )}

              {step === 3 && (
                <div className="text-sm text-muted-foreground space-y-3">
                  <p>Run a live test using the parameters you entered. A healthy connection validates credentials and confirms reachability.</p>
                  <Button size="sm" onClick={() => testConnector(wizard, form)} disabled={testing} data-testid="wizard-test-btn" className="bg-navy text-white hover:bg-navy/90">
                    {testing ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Testing…</> : "Run connection test"}
                  </Button>
                </div>
              )}

              {step === 4 && <p className="text-sm text-muted-foreground">Go live. This connector will begin feeding the unified AIEvent model and Cockpit views. Parameters will be saved securely.</p>}
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)}><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
              {step < wizard.wizard_steps.length - 1
                ? <Button onClick={() => setStep(step + 1)} data-testid="wizard-next">Next <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
                : <Button onClick={finish} data-testid="wizard-finish">Save & go live</Button>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
